<h2 mat-dialog-title>{{ getDialogTitle() }}</h2>
<mat-dialog-content>
<form #formElement
      id="workflow-transition-form"
      [formGroup]="model.form">
    <fieldset class="column">
        <mat-form-field appearance="outline">
            <mat-label i18n>Selected Role(s)</mat-label>
            <mat-select formControlName="roles" [compareWith]="compareOptionsByID" multiple required>
                <mat-optgroup *ngFor="let group of model.roles" [label]="group.name">
                    <mat-option *ngFor="let role of group.items" [value]="role" [title]="role.name">{{ role.name }}</mat-option>
                </mat-optgroup>
            </mat-select>
            <mat-hint i18n>Which role(s) can perform the action?</mat-hint>
            <mat-error *ngIf="shouldShowError('roles')" i18n>Please select at least one role.</mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline">
            <mat-label i18n>Transition name</mat-label>
            <input matInput
                   formControlName="name"
                   placeholder="Type name of the action role performs..."
                   required>
            <mat-hint i18n>This will be the name of the action button shown on the form.</mat-hint>
            <mat-error *ngIf="shouldShowError('name')" i18n>Please provide a name for the transition.</mat-error>
        </mat-form-field>

        <fieldset class="states">
            <div class="start-state">
                <mat-form-field appearance="outline">
                    <mat-label i18n>Start state</mat-label>
                    <mat-select formControlName="source"
                                [compareWith]="compareOptionsByID"
                                required>
                        <mat-option *ngFor="let state of model.states"
                                    [value]="state">{{ state.name }}</mat-option>
                    </mat-select>
                    <mat-hint align="start" i18n>The transition starts in this state.</mat-hint>
                    <mat-hint *ngIf="model.canRenameSource" align="end">
                        <a href="" (click)="model.startRenameSourceState(); false" tabindex="-1" i18n>Rename state</a>
                    </mat-hint>
                    <mat-error *ngIf="shouldShowError('source')" i18n>You must select start state.</mat-error>
                </mat-form-field>

                <mat-form-field *ngIf="model.isAddingSource"
                                appearance="outline"
                                floatLabel="always">
                    <mat-label i18n>New state</mat-label>
                    <input matInput
                           formControlName="sourceNew"
                           placeholder="Type name of the state"
                           required>
                    <mat-error *ngIf="shouldShowError('validSourceState')" i18n>You must select start state.</mat-error>
                </mat-form-field>

                <mat-form-field *ngIf="model.isRenamingSource"
                                appearance="outline"
                                floatLabel="always"
                                class="mt-5">
                    <mat-label i18n>The {{ model.form.value.source.name }} state will be renamed to</mat-label>
                    <input matInput
                           formControlName="sourceNew"
                           placeholder="Type new name for the state"
                           required>
                    <button mat-icon-button matSuffix
                            (click)="model.cancelRenameSourceState()"
                            aria-label="Cancel renaming"
                            [attr.aria-pressed]="cancel"
                            i18n-aria-label>
                        <mat-icon>cancel</mat-icon>
                    </button>
                    <mat-error *ngIf="shouldShowError('validSourceState')" i18n>You must select start state.</mat-error>
                </mat-form-field>
            </div>
            <div class="arrow">&#8594;</div>
            <div class="end-state">
                <mat-form-field appearance="outline">
                    <mat-label i18n>End state</mat-label>
                    <mat-select formControlName="target" [compareWith]="compareOptionsByID" required>
                        <mat-option *ngFor="let state of model.states" [value]="state">{{ state.name }}</mat-option>
                    </mat-select>
                    <mat-hint align="start" i18n>The transition finishes in this state.</mat-hint>
                    <mat-hint *ngIf="model.canRenameTarget" align="end">
                        <a href="" (click)="model.startRenameTargetState(); false" tabindex="-1" i18n>Rename state</a>
                    </mat-hint>
                    <mat-error *ngIf="shouldShowError('target')" i18n>You must select end state.</mat-error>
                </mat-form-field>

                <mat-form-field *ngIf="model.isAddingTarget"
                                appearance="outline"
                                floatLabel="always"
                                class="mt-5">
                    <mat-label i18n>New state</mat-label>
                    <input matInput
                           formControlName="targetNew"
                           placeholder="Type name of the state"
                           required>
                    <mat-error *ngIf="shouldShowError('validTargetState')" i18n>You must select end state.</mat-error>
                </mat-form-field>

                <mat-form-field *ngIf="model.isRenamingTarget"
                                appearance="outline"
                                floatLabel="always">
                    <mat-label i18n>The {{ model.form.value.target.name }} state will be renamed to</mat-label>
                    <input matInput
                           formControlName="targetNew"
                           placeholder="Type new name for the state"
                           required>
                    <button mat-icon-button matSuffix
                            (click)="model.cancelRenameTargetState()"
                            aria-label="Cancel renaming"
                            [attr.aria-pressed]="cancel"
                            i18n-aria-label>
                        <mat-icon>cancel</mat-icon>
                    </button>
                    <mat-error *ngIf="shouldShowError('validTargetState')"></mat-error>
                </mat-form-field>
            </div>
        </fieldset>
    </fieldset>

    <fieldset>
        <fui-fieldset-legend label="Completion settings" i18n-label></fui-fieldset-legend>
        <mat-form-field appearance="outline" class="pt-5">
            <mat-label i18n>Success message</mat-label>
            <textarea matInput formControlName="message"></textarea>
            <mat-hint i18n>This message is shown to the user at the top of the form upon a successful completion of this transition.</mat-hint>
        </mat-form-field>
    </fieldset>

    <fieldset>
        <fui-fieldset-legend label="Behaviour settings" i18n-label></fui-fieldset-legend>
        <div class="fry-checkbox-field">
            <mat-checkbox formControlName="autoTransition" i18n>Automatically trigger this transition</mat-checkbox>
            <small i18n>When enabled this transition will be actioned automatically once ALL guards specified on this transition are satisfied.</small>
        </div>
        <div class="fry-checkbox-field">
            <mat-checkbox formControlName="timeTriggeredTransition" i18n>Automatically trigger this transition after specific time of inactivity</mat-checkbox>
            <small i18n>When enabled this transition will be actioned automatically once a time limit of inactivity is reached.</small>
        </div>
        <div *ngIf="model.isTimeTriggered" model.class="fry-checkbox-field">
            <mat-form-field appearance="outline">
                <mat-label i18n>Expiration time (in minutes)</mat-label>
                <input matInput formControlName="timeExpiration" type="number" placeholder="Expiration time (in minutes)">
            </mat-form-field>
        </div>
        <div class="fry-checkbox-field">
            <mat-checkbox formControlName="requireValidForm" i18n>Ensure all mandatory fields are complete</mat-checkbox>
            <small i18n>When enabled all mandatory fields on the form must be complete for the transition to occur.</small>
        </div>

        <ng-container formGroupName="confirmation">
            <div class="fry-checkbox-field">
                <mat-checkbox formControlName="requireConfirmation" i18n>Confirmation required</mat-checkbox>
                <small i18n>When enabled the user performing the transition will be asked to confirm this action from a popup.</small>
            </div>

            <fieldset *ngIf="model.isConfirmationRequired">
                <mat-form-field appearance="outline">
                    <mat-label i18n>Confirmation title</mat-label>
                    <input matInput formControlName="title" placeholder="Title of confirmation request">
                </mat-form-field>

                <mat-form-field appearance="outline">
                    <mat-label i18n>Confirmation description</mat-label>
                    <input matInput formControlName="description" placeholder="Description of confirmation request">
                </mat-form-field>

                <div class="confirmation-button-titles">
                    <mat-form-field appearance="outline">
                        <mat-label i18n>Cancel button title</mat-label>
                        <input matInput formControlName="cancelButtonTitle" placeholder="Name of cancel button">
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                        <mat-label i18n>Continue button title</mat-label>
                        <input matInput formControlName="continueButtonTitle" placeholder="Name of continue button">
                    </mat-form-field>
                </div>
            </fieldset> <!-- /confirmation details -->
        </ng-container> <!-- /confirmation -->
    </fieldset>
</form>
</mat-dialog-content>

<mat-dialog-actions align="end">
    <button mat-button tabindex="-1" (click)="onCancel()" i18n>Discard</button>
    <button mat-flat-button type="submit"
                            color="primary"
                            (click)="onSave()"
                            [disabled]="!model.isValid()"
                            i18n>Apply</button>
</mat-dialog-actions>
